import { useEffect } from 'react';

const SITE_URL = 'https://brow-mie.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const setMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const absoluteUrl = (path) => {
  if (!path || path === '/') return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export default function Seo({
  title,
  description,
  canonicalPath = '/',
  robots = 'index, follow, max-image-preview:large',
  image = DEFAULT_IMAGE,
  imageAlt = 'Browmie homemade brownies and cookies',
  ogType = 'website',
  jsonLd = [],
}) {
  const canonical = absoluteUrl(canonicalPath);
  const absoluteImage = absoluteUrl(image);
  const jsonLdKey = JSON.stringify(jsonLd.filter(Boolean));

  useEffect(() => {
    document.documentElement.lang = 'th';
    document.title = title;
    setCanonical(canonical);
    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:site_name', 'Browmie');
    setMeta('property', 'og:locale', 'th_TH');
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', absoluteImage);
    setMeta('property', 'og:image:alt', imageAlt);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', absoluteImage);

    const structuredData = JSON.parse(jsonLdKey);
    const dynamicTypes = new Set(structuredData.map((value) => value?.['@type']).filter(Boolean));
    document.head.querySelectorAll('script[data-seo-static="route"]').forEach((script) => {
      try {
        if (dynamicTypes.has(JSON.parse(script.textContent)?.['@type'])) script.remove();
      } catch { /* an invalid static block should not stop page metadata */ }
    });

    const scripts = structuredData.map((value, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seoJsonld = 'dynamic';
      script.dataset.seoIndex = String(index);
      script.textContent = JSON.stringify(value).replace(/</g, '\\u003c');
      document.head.appendChild(script);
      return script;
    });

    return () => scripts.forEach((script) => script.remove());
  }, [absoluteImage, canonical, description, imageAlt, jsonLdKey, ogType, robots, title]);

  return null;
}
